import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * NPSModal.jsx — Encuesta NPS post-descarga del Diagnóstico.
 * Compliance Ley 1581 estricta: checkbox de consentimiento OBLIGATORIO, NO pre-checked.
 * 
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   caseId: string
 *   nivel: "nivel1" | "nivel2"
 *   onSubmitted: () => void (opcional, callback post-envío exitoso)
 */
export default function NPSModal({ open, onClose, caseId, nivel, onSubmitted }) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (score === null) {
      toast.error("Selecciona un puntaje de 0 a 10");
      return;
    }
    if (!consentChecked) {
      toast.error("Debes aceptar el consentimiento para enviar tu opinión");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/cases/${caseId}/nps`, {
        score,
        comment: comment.trim() || null,
        consent_given: consentChecked,
        nivel,
      });
      toast.success("¡Gracias por tu opinión!");
      onSubmitted?.();
      handleClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo enviar tu opinión");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setScore(null);
    setComment("");
    setConsentChecked(false);
    onClose();
  };

  const getScoreColor = (s) => {
    if (s <= 6) return "bg-red-100 text-red-900 hover:bg-red-200 border-red-300";
    if (s <= 8) return "bg-amber-100 text-amber-900 hover:bg-amber-200 border-amber-300";
    return "bg-emerald-100 text-emerald-900 hover:bg-emerald-200 border-emerald-300";
  };

  const isSubmitDisabled = score === null || !consentChecked || submitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="nps-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-gray-900">
            ¿Qué tan probable es que recomiendes JustSapiens Procure?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Escala 0-10 */}
          <div>
            <div className="flex justify-between mb-2 text-xs text-gray-600">
              <span>Nada probable</span>
              <span>Extremadamente probable</span>
            </div>
            <div className="grid grid-cols-11 gap-2">
              {[...Array(11)].map((_, i) => (
                <button
                  key={`nps-btn-${i}`}
                  type="button"
                  onClick={() => setScore(i)}
                  disabled={submitting}
                  className={`h-12 rounded-sm border-2 font-semibold transition-all ${
                    score === i
                      ? `${getScoreColor(i)} ring-2 ring-offset-2 ring-primary`
                      : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  data-testid={`nps-score-${i}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea opcional */}
          <div>
            <label htmlFor="nps-comment" className="block text-sm font-medium text-gray-900 mb-2">
              ¿Qué podríamos mejorar? (opcional)
            </label>
            <textarea
              id="nps-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
              disabled={submitting}
              placeholder="Comparte tus comentarios o sugerencias..."
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              data-testid="nps-comment"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {comment.length}/1000 caracteres
            </div>
          </div>

          {/* Checkbox de consentimiento (Ley 1581) */}
          <div className="border border-gray-300 bg-gray-50 p-4 rounded-sm">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                disabled={submitting}
                className="mt-0.5 w-4 h-4 text-primary focus:ring-primary rounded border-gray-300"
                data-testid="nps-consent"
              />
              <span className="text-sm text-gray-900">
                Acepto compartir mi opinión de forma anónima para que JustSapiens mejore Procure. 
                Mis datos serán tratados conforme a la{" "}
                <a
                  href="/politica-datos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80"
                >
                  Política de Tratamiento de Datos
                </a>
                .
              </span>
            </label>
          </div>

          {/* Botones */}
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              className="rounded-sm"
              data-testid="nps-later"
            >
              Más tarde
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className="rounded-sm"
              data-testid="nps-submit"
            >
              {submitting ? "Enviando…" : "Enviar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
